import { useCallback, useEffect, useState } from "react";
import { Eye, History, ShieldCheck, X } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import { readAdminToken } from "@/components/admin/AdminGuard";
import { DISCIPLINE_IDS } from "@/lib/disciplines";
import { useT } from "@/i18n/LanguageContext";

/**
 * /admin/library — Living Library CMS (Founder Publishing workflow).
 * Completely separate from the Journal Publishing Studio.
 * Journeys list · status workflow (draft → review → published) · preview ·
 * Founder Approval publish gate · version history · completeness indicators.
 */

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const STATUS_STYLE = {
  draft: "text-bone/60 border-bone/25",
  review: "text-amber-300/90 border-amber-300/40",
  published: "text-emerald-300/90 border-emerald-300/40",
  archived: "text-bone/35 border-bone/15",
};

function Dot({ ok }) {
  return (
    <span
      className={`inline-block w-2 h-2 rounded-full ${ok ? "bg-emerald-400/90" : "bg-red-400/70"}`}
      aria-hidden
    />
  );
}

function Indicator({ label, ok, testid }) {
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.14em] uppercase text-bone/60">
      <Dot ok={ok} /> {label}
    </span>
  );
}

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("nl-NL", { dateStyle: "medium", timeStyle: "short" }) : "—");

export default function AdminLibrary() {
  const { t } = useT();
  const token = readAdminToken();
  const [journeys, setJourneys] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [approval, setApproval] = useState(null); // journey pending Founder Approval
  const [approvalChecked, setApprovalChecked] = useState(false);
  const [publishError, setPublishError] = useState("");
  const [history, setHistory] = useState(null); // { journey, versions }

  const load = useCallback(() => {
    fetch(`${API}/api/admin/library/journeys`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => setJourneys(d.journeys))
      .catch((e) => setError(String(e.message || e)));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const applyPendingSeed = async (j) => {
    setBusy(j.id);
    setPublishError("");
    try {
      const res = await fetch(`${API}/api/admin/library/entries/${j.id}/apply-pending-seed`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      load();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(null);
    }
  };

  const setStatus = async (j, status) => {
    setBusy(j.id);
    setPublishError("");
    try {
      const res = await fetch(`${API}/api/admin/library/entries/${j.id}/status`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (res.status === 422) {
        const d = await res.json();
        const fields = d?.detail?.fields || [];
        setPublishError(`${t("admin.libcms.publishBlocked")} ${fields.join(" · ")}`);
        return false;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      load();
      return true;
    } catch (e) {
      setPublishError(String(e.message || e));
      return false;
    } finally {
      setBusy("");
    }
  };

  const confirmPublish = async () => {
    if (!approval || !approvalChecked) return;
    const ok = await setStatus(approval, "published");
    if (ok) {
      setApproval(null);
      setApprovalChecked(false);
    }
  };

  const openHistory = async (j) => {
    const res = await fetch(`${API}/api/admin/library/entries/${j.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const doc = res.ok ? await res.json() : {};
    setHistory({ journey: j, versions: [...(doc.versions || [])].reverse() });
  };

  // Canonical discipline (Founder architecture): every Journey belongs to
  // exactly one of the Three Disciplines — logic | physics | ethics.
  const setDiscipline = async (j, discipline) => {
    setBusy(j.id);
    try {
      const res = await fetch(`${API}/api/admin/library/entries/${j.id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ discipline }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      load();
    } catch (e) {
      setPublishError(String(e.message || e));
    } finally {
      setBusy("");
    }
  };

  // Post-deployment Journey Sync (Founder): dry-run first, then explicit
  // confirmation. Production-safe — the backend never changes statuses or
  // overwrites Founder content (journey_sync.py).
  const [sync, setSync] = useState(null); // {phase:'preview'|'done', report}
  const runSync = async (dryRun) => {
    setBusy("sync");
    try {
      const res = await fetch(`${API}/api/admin/journeys/sync?dryRun=${dryRun}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const report = await res.json();
      setSync({ phase: dryRun ? "preview" : "done", report });
      if (!dryRun) load();
    } catch (e) {
      setPublishError(String(e.message || e));
    } finally {
      setBusy("");
    }
  };

  return (
    <AdminShell title={t("admin.libcms.title")}>
      <div className="-mt-6 mb-8 flex flex-wrap items-start gap-4">
        <p className="text-sm text-bone/60 max-w-2xl">{t("admin.libcms.intro")}</p>
        <button
          type="button"
          data-testid="libcms-sync-btn"
          onClick={() => runSync(true)}
          disabled={busy === "sync"}
          className="ml-auto border border-gold/40 text-gold px-4 py-2 text-[11px] tracking-[0.22em] uppercase hover:bg-gold/10 transition-colors disabled:opacity-50"
        >
          {t("admin.libcms.sync")}
        </button>
      </div>

      {sync && (
        <div data-testid="libcms-sync-panel" className="mb-8 border border-gold/25 bg-white/[0.02] p-5">
          <p className="text-[11px] tracking-[0.22em] uppercase text-gold/80 mb-3">
            {sync.phase === "preview" ? t("admin.libcms.sync.preview") : t("admin.libcms.sync.done")}
          </p>
          <ul className="space-y-1.5">
            {sync.report.results.map((r) => (
              <li key={r.slug} data-testid={`libcms-sync-result-${r.slug}`} className="text-sm text-bone/80">
                <span className="font-serif text-bone capitalize">{r.slug}</span>
                <span className="text-gold/80"> · {r.action}</span>
                <span className="text-bone/50"> · status: {r.status || "—"}</span>
                {r.detail ? <span className="block text-[11px] text-bone/45">{r.detail}</span> : null}
              </li>
            ))}
          </ul>
          <p className="mt-3 text-[11px] text-bone/40">commit: {sync.report.commit} · {sync.report.at}</p>
          <div className="mt-4 flex gap-3">
            {sync.phase === "preview" ? (
              <>
                <button
                  type="button"
                  data-testid="libcms-sync-confirm"
                  onClick={() => runSync(false)}
                  disabled={busy === "sync"}
                  className="border border-gold bg-gold/15 text-gold px-4 py-2 text-[11px] tracking-[0.22em] uppercase hover:bg-gold/25 transition-colors disabled:opacity-50"
                >
                  {t("admin.libcms.sync.confirm")}
                </button>
                <button
                  type="button"
                  data-testid="libcms-sync-cancel"
                  onClick={() => setSync(null)}
                  className="border border-white/15 text-bone/60 px-4 py-2 text-[11px] tracking-[0.22em] uppercase hover:border-white/30 transition-colors"
                >
                  {t("admin.libcms.sync.cancel")}
                </button>
              </>
            ) : (
              <button
                type="button"
                data-testid="libcms-sync-close"
                onClick={() => setSync(null)}
                className="border border-white/15 text-bone/60 px-4 py-2 text-[11px] tracking-[0.22em] uppercase hover:border-white/30 transition-colors"
              >
                {t("admin.libcms.sync.close")}
              </button>
            )}
          </div>
        </div>
      )}

      {error && (
        <div data-testid="libcms-error" className="mb-6 border border-red-400/40 bg-red-400/10 px-4 py-3 text-sm text-red-200">
          {error}
        </div>
      )}
      {publishError && !approval && (
        <div data-testid="libcms-publish-error" className="mb-6 border border-amber-300/40 bg-amber-300/10 px-4 py-3 text-sm text-amber-200">
          {publishError}
        </div>
      )}

      <div className="space-y-4" data-testid="libcms-journeys">
        {journeys === null && <p className="text-sm text-bone/50">…</p>}
        {journeys?.length === 0 && <p className="text-sm text-bone/50">{t("admin.libcms.empty")}</p>}
        {journeys?.map((j) => (
          <div
            key={j.id}
            data-testid={`libcms-journey-${j.slug}`}
            className="border border-gold/15 bg-white/[0.02] p-5 md:p-6 hover:border-gold/35 transition-colors"
          >
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span className="text-[10px] tracking-[0.28em] uppercase text-gold/70">
                Journey {String(j.order).padStart(3, "0")}
              </span>
              <h3 className="font-serif text-xl text-bone">
                {j.title?.en || j.slug}
                {j.title?.nl ? <span className="text-bone/45"> · {j.title.nl}</span> : null}
              </h3>
              <span
                data-testid={`libcms-status-${j.slug}`}
                className={`ml-auto border px-2.5 py-1 text-[10px] tracking-[0.22em] uppercase ${STATUS_STYLE[j.status] || STATUS_STYLE.draft}`}
              >
                {t(`admin.libcms.status.${j.status}`)}
              </span>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
              <Indicator testid={`libcms-bilingual-${j.slug}`} label="EN/NL" ok={j.bilingual.complete} />
              <Indicator testid={`libcms-hero-${j.slug}`} label={t("admin.libcms.hero")} ok={j.hero.mobile && j.hero.desktop} />
              <Indicator label={t("admin.libcms.lexicon")} ok={j.completeness.lexicon} />
              <Indicator label={t("admin.libcms.reflection")} ok={j.completeness.reflection.en && j.completeness.reflection.nl} />
              <Indicator label={t("admin.libcms.practice")} ok={j.completeness.practice.en && j.completeness.practice.nl} />
              <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.14em] uppercase text-bone/60">
                {t("admin.libcms.discipline")}
                <select
                  value={j.discipline || ""}
                  onChange={(e) => setDiscipline(j, e.target.value)}
                  disabled={busy === j.id}
                  data-testid={`libcms-discipline-${j.slug}`}
                  className="bg-ink border border-gold/25 text-gold/90 text-[10px] tracking-[0.14em] uppercase px-2 py-1 focus:outline-none focus:border-gold/60"
                >
                  <option value="">{t("admin.libcms.discipline.none")}</option>
                  {DISCIPLINE_IDS.map((d) => (
                    <option key={d} value={d}>{t(`admin.libcms.discipline.${d}`)}</option>
                  ))}
                </select>
              </span>
            </div>
            {j.bilingual.problems?.length > 0 && (
              <p className="mt-2 text-[11px] text-amber-200/80">{j.bilingual.problems.join(" · ")}</p>
            )}

            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-[11px] text-bone/50">
              <span data-testid={`libcms-published-at-${j.slug}`}>{t("admin.libcms.publishedAt")}: {fmt(j.published_at)}</span>
              <span>{t("admin.libcms.modified")}: {fmt(j.updated_at)}</span>
              <span>{t("admin.libcms.versions")}: {j.versions_count}</span>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <a
                href={`/library/virtues/${j.slug}?preview=1`}
                target="_blank"
                rel="noreferrer"
                data-testid={`libcms-preview-${j.slug}`}
                className="inline-flex items-center gap-2 border border-bone/25 px-3.5 py-1.5 text-[10px] tracking-[0.22em] uppercase text-bone/70 hover:border-gold/50 hover:text-gold transition-colors"
              >
                <Eye size={12} /> {t("admin.libcms.preview")}
              </a>
              {j.pending_seed_version ? (
                <button
                  onClick={() => applyPendingSeed(j)}
                  disabled={busy === j.id}
                  data-testid={`libcms-apply-seed-${j.slug}`}
                  className="border border-gold/50 bg-gold/10 px-3.5 py-1.5 text-[10px] tracking-[0.22em] uppercase text-gold hover:bg-gold/20 transition-colors"
                >
                  {t("admin.libcms.applySeed")} (v{j.pending_seed_version})
                </button>
              ) : null}
              {j.status === "draft" && (
                <button
                  onClick={() => setStatus(j, "review")}
                  disabled={busy === j.id}
                  data-testid={`libcms-to-review-${j.slug}`}
                  className="border border-amber-300/40 px-3.5 py-1.5 text-[10px] tracking-[0.22em] uppercase text-amber-200/90 hover:bg-amber-300/10 transition-colors"
                >
                  {t("admin.libcms.toReview")}
                </button>
              )}
              {j.status !== "published" && (
                <button
                  onClick={() => { setApproval(j); setApprovalChecked(false); setPublishError(""); }}
                  disabled={busy === j.id}
                  data-testid={`libcms-publish-${j.slug}`}
                  className="border border-emerald-300/40 px-3.5 py-1.5 text-[10px] tracking-[0.22em] uppercase text-emerald-200/90 hover:bg-emerald-300/10 transition-colors"
                >
                  {t("admin.libcms.publish")}
                </button>
              )}
              {j.status === "published" && (
                <button
                  onClick={() => setStatus(j, "draft")}
                  disabled={busy === j.id}
                  data-testid={`libcms-unpublish-${j.slug}`}
                  className="border border-red-400/40 px-3.5 py-1.5 text-[10px] tracking-[0.22em] uppercase text-red-200/90 hover:bg-red-400/10 transition-colors"
                >
                  {t("admin.libcms.unpublish")}
                </button>
              )}
              <button
                onClick={() => openHistory(j)}
                data-testid={`libcms-history-${j.slug}`}
                className="inline-flex items-center gap-2 border border-bone/25 px-3.5 py-1.5 text-[10px] tracking-[0.22em] uppercase text-bone/70 hover:border-gold/50 hover:text-gold transition-colors"
              >
                <History size={12} /> {t("admin.libcms.history")}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Founder Approval — the publish gate */}
      {approval && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-6" data-testid="libcms-approval-modal">
          <div className="w-full max-w-lg border border-gold/30 bg-ink p-7">
            <div className="flex items-center gap-3 mb-4">
              <ShieldCheck size={18} className="text-gold" />
              <h3 className="font-serif text-xl text-bone">{t("admin.libcms.approvalTitle")}</h3>
              <button onClick={() => setApproval(null)} className="ml-auto text-bone/50 hover:text-bone" data-testid="libcms-approval-close">
                <X size={16} />
              </button>
            </div>
            <p className="text-sm text-bone/70 leading-relaxed">
              {t("admin.libcms.approvalText")} <strong className="text-bone">{approval.title?.en || approval.slug}</strong>.
            </p>
            {publishError && (
              <p data-testid="libcms-approval-error" className="mt-4 border border-amber-300/40 bg-amber-300/10 px-3 py-2 text-xs text-amber-200">
                {publishError}
              </p>
            )}
            <label className="mt-5 flex items-start gap-3 text-sm text-bone/80 cursor-pointer">
              <input
                type="checkbox"
                checked={approvalChecked}
                onChange={(e) => setApprovalChecked(e.target.checked)}
                data-testid="libcms-approval-checkbox"
                className="mt-0.5 accent-[#C9A961]"
              />
              {t("admin.libcms.approvalCheck")}
            </label>
            <div className="mt-6 flex gap-3">
              <button
                onClick={confirmPublish}
                disabled={!approvalChecked || busy === approval.id}
                data-testid="libcms-approval-confirm"
                className="border border-emerald-300/50 px-5 py-2 text-[11px] tracking-[0.22em] uppercase text-emerald-200 disabled:opacity-40 hover:bg-emerald-300/10 transition-colors"
              >
                {t("admin.libcms.confirmPublish")}
              </button>
              <button
                onClick={() => setApproval(null)}
                className="border border-bone/25 px-5 py-2 text-[11px] tracking-[0.22em] uppercase text-bone/60 hover:text-bone transition-colors"
              >
                {t("admin.libcms.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Version History */}
      {history && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-6" data-testid="libcms-history-modal">
          <div className="w-full max-w-xl border border-gold/30 bg-ink p-7 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-5">
              <History size={16} className="text-gold" />
              <h3 className="font-serif text-xl text-bone">
                {t("admin.libcms.historyTitle")} · {history.journey.title?.en || history.journey.slug}
              </h3>
              <button onClick={() => setHistory(null)} className="ml-auto text-bone/50 hover:text-bone" data-testid="libcms-history-close">
                <X size={16} />
              </button>
            </div>
            {history.versions.length === 0 && <p className="text-sm text-bone/50">{t("admin.libcms.noVersions")}</p>}
            <ol className="space-y-3">
              {history.versions.map((v, i) => (
                <li key={i} className="border border-bone/15 px-4 py-3 text-sm text-bone/75 flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="text-gold/70 text-[10px] tracking-[0.2em] uppercase">
                    v{history.versions.length - i}{v.event === "publish" ? ` · ${t("admin.libcms.publishEvent")}` : ""}
                  </span>
                  <span>{v.title?.en || "—"}</span>
                  <span className="ml-auto text-[11px] text-bone/45">{fmt(v.saved_at)} · {v.status}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
